/**
 * PharmacyAccountMenu.tsx — the pharmacy console's top-bar account control.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY a sibling of `ui/chrome.tsx`'s ProfileMenu rather than a branch inside it:
 * ProfileMenu reads `useAuth()`, and a pharmacist has no entry in that context
 * (see pharmacy/session.ts). Adding a fourth role branch would mean teaching the
 * patient/staff account menu about a session store it must otherwise never
 * touch. The LOOK is shared, not forked — the same `Menu` primitive and the same
 * exported `profileTriggerStyle` pill, so the two consoles are visually
 * identical.
 *
 * SIGNING OUT is the ONLY item. There is no profile screen to link to: a
 * pharmacist account has no self-service surface (accounts are provisioned by an
 * admin with a pre-issued code), and there is deliberately no plans or money
 * entry anywhere on this console.
 */
import { Menu, MenuItem } from '../ui/Menu';
import { profileTriggerStyle } from '../ui/chrome';
import type { PharmacySession } from './session';

export function PharmacyAccountMenu({ session, onSignOut }: { session: PharmacySession; onSignOut: () => void }) {
  // The named professional when identity sends it; otherwise a neutral role
  // label. Never the pharmacist's raw user id — an id is not a name, and a
  // pharmacist reading "a3f1…" would reasonably wonder whose session they are on.
  const displayName = session.fullName?.trim() || 'Pharmacist';
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <Menu
      menuLabel="Account menu"
      wrapperStyle={{ justifySelf: 'end' }}
      triggerStyle={profileTriggerStyle}
      menuStyle={{ minWidth: '10rem' }}
      triggerContent={
        <>
          <span
            aria-hidden="true"
            style={{
              width: '28px',
              height: '28px',
              flex: 'none',
              borderRadius: 'var(--gps-r-pill)',
              background: 'var(--gps-primary)',
              color: 'var(--gps-on-accent)',
              display: 'grid',
              placeItems: 'center',
              fontSize: '13px',
              fontWeight: 700,
            }}
          >
            {initial}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
        </>
      }
    >
      {({ close }) => (
        <MenuItem
          onSelect={() => {
            close();
            onSignOut();
          }}
        >
          Sign out
        </MenuItem>
      )}
    </Menu>
  );
}
